package com.example.mystybag.appCore.views

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.LocalMinimumInteractiveComponentSize
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.CitySelectionBottomSheetViewModel
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.views.WebDialogCloseButton
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.setSoftInputModeOverlay
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.views.AppTheme
import kotlinx.coroutines.delay

@Composable
fun CitySelectionBottomSheetView(viewModel: CitySelectionBottomSheetViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val state = viewModel.uiState.subscribeAsState()

    DisposableEffect(Unit) {
        setSoftInputModeOverlay(overlay = true)
        onDispose { setSoftInputModeOverlay(overlay = false) }
    }
    LaunchedEffect(Unit) {
        delay(150)
        setSoftInputModeOverlay(overlay = true)
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
        ) {
            // Title – City (380x44)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p16)
            ) {
                Texts.TitleLarge(
                    text = sharedString(SR.strings.city),
                    textAlign = TextAlign.Center,
                    color = AppTheme.current.colors.onSurface,
                    modifier = Modifier.align(Alignment.Center)
                )
                if (PlatformHelper.isWeb) {
                    WebDialogCloseButton(
                        modifier = Modifier
                            .align(Alignment.CenterEnd)
                            .size(40.dp),
                        debugName = "CitySelectionBottomSheetView",
                        onClick = {
                            LogHelper.logEvent("[WebDialogCloseButton] CitySelectionBottomSheetView parentCloseBottomSheet requested")
                            viewModel.parentCloseBottomSheet()
                        }
                    )
                }
            }
            HorizontalDivider(
                modifier = Modifier.padding(top = p16),
                thickness = 1.dp,
                color = AppTheme.current.colors.outlineVariant
            )

            // Description – Select the city where you'd like to collect your MystyBags. (itemSpacing 32 → 16 in Figma frame)
            Texts.BodyMedium(
                text = sharedString(SR.strings.city_select_description),
                textAlign = TextAlign.Start,
                color = AppTheme.current.colors.onSurfaceVariant,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p32)
            )

            // Search Input – pill shape cornerRadius 100, 48dp height
            DefaultCircleShapedTextField(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p24),
                value = state.value.searchText,
                label = sharedString(SR.strings.search_city),
                onValueChange = viewModel::onSearchTextChanged,
                inputType = TextFieldType.ANY,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Text,
                    imeAction = ImeAction.Search
                )
            )

            // City list – 24dp itemSpacing between search and list (Figma Frame 26080447)
            if (state.value.isLoading) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p32),
                    horizontalArrangement = Arrangement.Center
                ) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(p48),
                        color = AppTheme.current.colors.primary
                    )
                }
            } else {
                val filteredCities = if (state.value.searchText.isEmpty()) {
                    state.value.cities
                } else {
                    state.value.cities.filter {
                        it.cityName.contains(state.value.searchText, ignoreCase = true)
                    }
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p24),
                    verticalArrangement = Arrangement.spacedBy(p0)
                ) {
                    filteredCities.forEachIndexed { index, city ->
                        if (index > 0) {
                            HorizontalDivider(
                                modifier = Modifier.fillMaxWidth(),
                                thickness = 1.dp,
                                color = AppTheme.current.colors.outlineVariant.copy(alpha = 0.5f)
                            )
                        }
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(64.dp)
                                .clickable {
                                    keyboardController?.hide()
                                    viewModel.onCitySelected(city)
                                }
                                .padding(vertical = p20),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(p16)
                        ) {
                            CompositionLocalProvider(LocalMinimumInteractiveComponentSize provides p0) {
                                RadioButton(
                                    selected = state.value.selectedCity?.id == city.id,
                                    onClick = {
                                        keyboardController?.hide()
                                        viewModel.onCitySelected(city)
                                    },
                                    colors = RadioButtonDefaults.colors(
                                        selectedColor = AppTheme.current.colors.primary,
                                        unselectedColor = AppTheme.current.colors.outline
                                    )
                                )
                            }
                            Texts.BodyLarge(
                                text = city.cityName,
                                color = AppTheme.current.colors.onSurface,
                                modifier = Modifier.weight(1f)
                            )
                        }
                    }

                    if (filteredCities.isEmpty() && state.value.searchText.isNotEmpty()) {
                        Texts.BodyMedium(
                            text = sharedString(SR.strings.no_cities_found),
                            textAlign = TextAlign.Center,
                            color = AppTheme.current.colors.onSurfaceVariant,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = p16)
                        )
                    }
                }
            }
        }

        DefaultButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = p24, bottom = p32),
            caption = sharedString(SR.strings.save),
            callback = {
                keyboardController?.hide()
                viewModel.onSaveClicked()
            }
        )
    }
}
